import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  AlertTitle,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Cloud as CloudIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import AIUsageService, { AIUsageSummary, AIUsageAlert, AIUsageConfig } from '../../services/AIUsageService';
import AIUsageCharts from './AIUsageCharts';
import AIUsageConfiguration from './AIUsageConfiguration';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-usage-tabpanel-${index}`}
      aria-labelledby={`ai-usage-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AIUsageMonitor: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AIUsageSummary | null>(null);
  const [alerts, setAlerts] = useState<AIUsageAlert[]>([]);
  const [config, setConfig] = useState<AIUsageConfig | null>(null);
  const [period, setPeriod] = useState<number>(7);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryData, alertsData, configData] = await Promise.all([
        AIUsageService.getUsageSummary(period),
        AIUsageService.getAlerts(),
        AIUsageService.getConfiguration()
      ]);

      setSummary(summaryData);
      setAlerts(alertsData);
      setConfig(configData);
    } catch (err: any) {
      console.error('Failed to load AI usage data:', err);
      setError(err.response?.data?.message || 'Failed to load AI usage data');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event: SelectChangeEvent<number>) => {
    setPeriod(event.target.value as number);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = async () => {
    await AIUsageService.clearCache();
    await loadData();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading && !summary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !summary) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          AI Usage & Cost Monitor
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} label="Period" onChange={handlePeriodChange}>
              <MenuItem value={1}>Last 24h</MenuItem>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Box mb={3}>
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.severity === 'high' ? 'error' : alert.severity === 'medium' ? 'warning' : 'info'}
              icon={<WarningIcon />}
              sx={{ mb: 1 }}
            >
              <AlertTitle>{alert.title}</AlertTitle>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <AttachMoneyIcon color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Cost ({period} days)
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(summary.summary.total_cost_usd)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TrendingUpIcon color="success" fontSize="large" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Avg Daily Cost
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(summary.summary.average_daily_cost)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TrendingUpIcon color="warning" fontSize="large" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Projected Monthly
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(summary.summary.projected_monthly_cost)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CloudIcon color="info" fontSize="large" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Active Providers
                    </Typography>
                    <Typography variant="h5">
                      {summary.summary.active_providers}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Claude (Anthropic)" />
            <Tab label="OpenAI" />
            <Tab label="Configuration" icon={<SettingsIcon />} iconPosition="end" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {summary && <AIUsageCharts summary={summary} period={period} />}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {summary?.claude.status === 'active' && summary.claude.cost && summary.claude.usage ? (
            <Box>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Total Cost
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(summary.claude.cost.total_cost_usd)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Total Tokens
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(summary.claude.usage.total_tokens)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Avg Daily Tokens
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(summary.claude.usage.average_daily_tokens)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Token Breakdown
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Input</Typography>
                    <Typography variant="body1">{formatNumber(summary.claude.usage.input_tokens)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Output</Typography>
                    <Typography variant="body1">{formatNumber(summary.claude.usage.output_tokens)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cache Creation</Typography>
                    <Typography variant="body1">{formatNumber(summary.claude.usage.cache_creation_tokens)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cache Read</Typography>
                    <Typography variant="body1">{formatNumber(summary.claude.usage.cache_read_tokens)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Alert severity="info">
              <AlertTitle>Claude Usage Tracking Not Available</AlertTitle>
              {summary?.claude.message || 'Configure Claude Admin API key to enable usage tracking'}
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {summary?.openai.status === 'active' && summary.openai.usage ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Total Cost
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(summary.openai.usage.total_cost_usd)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Avg Daily Cost
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(summary.openai.usage.average_daily_cost)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Alert severity="info">
              <AlertTitle>OpenAI Usage Tracking Not Available</AlertTitle>
              {summary?.openai.message || 'Configure OpenAI API key to enable usage tracking'}
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {config && <AIUsageConfiguration config={config} onUpdate={loadData} />}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default AIUsageMonitor;
